<template>
    <div class="ticket-chat" @dragover.prevent="onDragOver" @dragleave="onDragLeave" @drop.prevent="onDrop">
        <header class="ticket-chat__header">
            <button v-if="showBack" type="button" class="ticket-chat__back" :title="$t('Back')" @click="$emit('back')">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div class="ticket-chat__title">
                <div class="ticket-chat__subject" :title="subject">{{ subject }}</div>
                <div class="ticket-chat__meta">
                    <span class="ticket-chat__status" :class="'ticket-chat__status--' + status">{{ statusLabel }}</span>
                    <span v-if="assigneeName">{{ assigneeName }}</span>
                    <span v-if="createDate" :title="formatFull(createDate)">{{ formatDay(createDate) }}</span>
                </div>
            </div>
            <div v-if="canManage && status != TicketStatus.WaitingForApprove && status != TicketStatus.Closed" class="ticket-chat__actions">
                <button v-if="!confirmingClose" type="button" class="ticket-chat__action" @click="confirmingClose = true">{{ $t('CloseTicketButton') }}</button>
                <template v-else>
                    <span class="ticket-chat__confirm">{{ $t('CloseTicketConfirm') }}</span>
                    <button type="button" class="ticket-chat__action ticket-chat__action--danger" :disabled="busy" @click="closeTicket">{{ $t('Yes') }}</button>
                    <button type="button" class="ticket-chat__action" @click="confirmingClose = false">{{ $t('No') }}</button>
                </template>
            </div>
        </header>

        <div ref="list" class="ticket-chat__list">
            <div v-if="loading" class="ticket-chat__skeleton">
                <div v-for="n in 4" :key="n" class="ticket-chat__skeleton-bubble" :class="{ 'ticket-chat__skeleton-bubble--mine': n % 2 == 0 }"></div>
            </div>
            <div v-else-if="error" class="ticket-chat__notice">
                <div>{{ error }}</div>
                <button type="button" class="ticket-chat__action" @click="load()">{{ $t('TryAgain') }}</button>
            </div>
            <template v-else>
                <template v-for="item in timeline">
                    <div v-if="item.type == 'day'" :key="item.key" class="ticket-chat__day">
                        <span>{{ dayLabel(item.date) }}</span>
                    </div>
                    <div v-else-if="item.type == 'event'" :key="item.key" class="ticket-chat__event">
                        <span :title="formatFull(item.message.sentDate)">{{ eventText(item.message) }} · {{ formatTime(item.message.sentDate) }}</span>
                    </div>
                    <div v-else :key="item.key" class="chat-row" :class="{ 'chat-row--mine': item.mine, 'chat-row--start': item.startsGroup }">
                        <div v-if="!item.mine" class="chat-avatar" :class="{ 'chat-avatar--hidden': !item.startsGroup }" :title="item.message.creatorName">
                            {{ initials(item.message.creatorName) }}
                        </div>
                        <div class="chat-column">
                            <div v-if="item.startsGroup && !item.mine" class="chat-author">{{ item.message.creatorName }}</div>
                            <div class="chat-bubble"
                                 :class="{
                                     'chat-bubble--mine': item.mine,
                                     'chat-bubble--start': item.startsGroup,
                                     'chat-bubble--end': item.endsGroup,
                                     'chat-bubble--pending': item.message.state == 'sending',
                                     'chat-bubble--failed': item.message.state == 'failed'
                                 }"
                                 :title="formatFull(item.message.sentDate)">
                                <div v-if="item.message.content" class="chat-bubble__text">{{ item.message.content }}</div>
                                <div v-if="hasImages(item.message)" class="chat-bubble__images">
                                    <chat-image v-for="id in item.message.resources" :key="id"
                                                :src="imageUrl(id)" :failed="imageFailed(id)"
                                                @visible="loadImage(id)" @open="openViewer(id)"/>
                                    <chat-image v-for="url in item.message.localPhotos" :key="url" :src="url" @open="openLocal(url)"/>
                                </div>
                            </div>
                            <div v-if="item.message.state == 'failed'" class="chat-time chat-time--failed">
                                {{ $t('MessageNotSent') }}
                                <button type="button" class="chat-retry" @click="retry(item.message)">{{ $t('TryAgain') }}</button>
                            </div>
                            <div v-else-if="item.message.state == 'sending'" class="chat-time">{{ $t('Sending') }}</div>
                            <div v-else-if="item.endsGroup" class="chat-time">{{ formatTime(item.message.sentDate) }}</div>
                        </div>
                    </div>
                </template>
                <div v-if="timeline.length == 0" class="ticket-chat__notice">{{ $t('NoMessages') }}</div>
            </template>
        </div>

        <div v-if="status == TicketStatus.WaitingForApprove && canManage" class="ticket-chat__banner">
            <span>{{ $t('TicketCloseRequested') }}</span>
            <div class="ticket-chat__banner-actions">
                <button type="button" class="ticket-chat__action ticket-chat__action--primary" :disabled="busy" @click="closeTicket">{{ $t('Accept') }}</button>
                <button type="button" class="ticket-chat__action" :disabled="busy" @click="denyClose">{{ $t('Deny') }}</button>
            </div>
        </div>

        <chat-composer v-if="status != TicketStatus.Closed" ref="composer" @send="send"/>
        <div v-else class="ticket-chat__closed">{{ $t('TicketClosedInfo') }}</div>

        <div v-if="dragging" class="ticket-chat__drop">{{ $t('DropPhotosHere') }}</div>

        <modal :showDialog="viewer != null" @close="viewer = null">
            <div v-if="viewer != null" class="chat-viewer">
                <button v-if="viewer.items.length > 1" type="button" class="chat-viewer__nav" @click="moveViewer(-1)">‹</button>
                <img v-if="viewerUrl" :src="viewerUrl" class="chat-viewer__img" alt="">
                <div v-else class="chat-viewer__placeholder"></div>
                <button v-if="viewer.items.length > 1" type="button" class="chat-viewer__nav" @click="moveViewer(1)">›</button>
                <div v-if="viewer.items.length > 1" class="chat-viewer__counter">{{ viewer.index + 1 }} / {{ viewer.items.length }}</div>
            </div>
        </modal>
    </div>
</template>

<script>
import { getMessagesForTicket, getMessageResource, sendMessage, readTicket, confirmRequestToClose, denyRequestToClose } from '@api'
import moment from 'moment'
import Modal from '@/components/Modal'
import ChatImage from './ChatImage.vue'
import ChatComposer from './ChatComposer.vue'
import { buildTimeline, parseUtc, initials, dayKey, EVENT_KEYS, STATUS_KEYS, TicketStatus } from './ticketChat'

// Wiadomości są rzadkie, więc zamiast websocketów lekkie odświeżanie otwartego wątku.
const REFRESH_MS = 60 * 1000
// Ile pikseli od dołu listy liczy się jeszcze jako „na dole” (wtedy nowe wiadomości przewijają).
const NEAR_BOTTOM_PX = 80

let localId = 0

export default {
    components: { Modal, ChatImage, ChatComposer },
    props: {
        // Wiersz z listy zgłoszeń (TicketsOverviewDto) — nagłówek jest z niego, zanim dojdzie wątek.
        ticket: { type: Object, required: true },
        showBack: { type: Boolean, default: false }
    },
    data() {
        return {
            TicketStatus,
            thread: null,
            messages: [],
            pending: [],
            // resourceId → { url, failed } — przeżywa przełączanie zgłoszeń, zwalniane w beforeDestroy.
            images: {},
            loading: false,
            error: '',
            busy: false,
            confirmingClose: false,
            dragging: false,
            viewer: null
        }
    },
    computed: {
        myUserId() {
            return this.$store.getters.user.userId
        },
        canManage() {
            // Zamykanie i akceptacja zamknięcia to endpointy Admin/Service.
            return !this.$store.getters.isUser
        },
        status() {
            return this.thread != null ? this.thread.status : this.ticket.status
        },
        statusLabel() {
            const key = STATUS_KEYS[this.status]
            return key ? this.$t(key) : ''
        },
        subject() {
            return this.thread != null ? this.thread.subject : this.ticket.subject
        },
        assigneeName() {
            return this.thread != null ? this.thread.assigneeName : this.ticket.assigneeName
        },
        createDate() {
            return parseUtc(this.thread != null ? this.thread.createDate : this.ticket.createDate)
        },
        timeline() {
            const pending = this.pending.filter(p => p.ticketId == this.ticket.id)
            return buildTimeline(this.messages.concat(pending), this.myUserId)
        },
        viewerUrl() {
            if (this.viewer == null) return null
            const item = this.viewer.items[this.viewer.index]
            return item.url || this.imageUrl(item.id)
        }
    },
    watch: {
        'ticket.id'() {
            this.confirmingClose = false
            this.viewer = null
            this.load()
        }
    },
    mounted() {
        this.load()
        this.refreshTimer = setInterval(this.refresh, REFRESH_MS)
        document.addEventListener('visibilitychange', this.refresh)
        window.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy() {
        clearInterval(this.refreshTimer)
        document.removeEventListener('visibilitychange', this.refresh)
        window.removeEventListener('keydown', this.onKeydown)
        Object.values(this.images).forEach(img => { if (img.url) URL.revokeObjectURL(img.url) })
        this.pending.forEach(p => p.localPhotos.forEach(url => URL.revokeObjectURL(url)))
    },
    methods: {
        async load({ silent = false } = {}) {
            const ticketId = this.ticket.id
            // Szybkie przełączanie zgłoszeń: odpowiedź dla poprzedniego nie może nadpisać bieżącego.
            const token = this.loadToken = (this.loadToken || 0) + 1

            if (!silent) {
                this.loading = true
                this.error = ''
                this.thread = null
                this.messages = []
            }

            const wasNearBottom = this.isNearBottom()

            try {
                const result = await getMessagesForTicket(ticketId)
                if (token !== this.loadToken) return

                const messages = result.messages.map(m => ({
                    id: m.id,
                    isEvent: m.isEvent,
                    content: m.content,
                    resources: (m.resources || []).filter(r => r != null),
                    sentDate: parseUtc(m.sentDate),
                    creatorId: m.creatorId,
                    creatorName: m.creatorName
                }))

                const knownIds = new Set(this.messages.map(m => m.id))
                const newFromOthers = messages.some(m => !knownIds.has(m.id) && m.creatorId !== this.myUserId)

                this.thread = result
                this.messages = messages

                if (!silent || wasNearBottom) {
                    this.$nextTick(this.scrollToBottom)
                }

                if (!silent || newFromOthers) {
                    readTicket(ticketId).then(() => this.$emit('read', ticketId))
                }
            } catch (e) {
                if (token === this.loadToken && !silent) {
                    this.error = this.$t('LoadingMessagesFailed')
                }
            } finally {
                if (token === this.loadToken) this.loading = false
            }
        },
        refresh() {
            if (document.hidden || this.loading || this.status == TicketStatus.Closed) return
            this.load({ silent: true })
        },

        async loadImage(id) {
            if (this.images[id] != null) return
            this.$set(this.images, id, { url: null, failed: false })

            try {
                const res = await getMessageResource(id)
                const url = URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }))
                this.$set(this.images, id, { url, failed: false })
            } catch (e) {
                this.$set(this.images, id, { url: null, failed: true })
            }
        },
        imageUrl(id) {
            const img = this.images[id]
            return img ? img.url : null
        },
        imageFailed(id) {
            const img = this.images[id]
            return img ? img.failed : false
        },
        hasImages(message) {
            return message.resources.length > 0 || (message.localPhotos != null && message.localPhotos.length > 0)
        },

        openViewer(id) {
            const items = []
            this.messages.forEach(m => m.resources.forEach(r => items.push({ id: r })))
            items.forEach(item => this.loadImage(item.id))
            this.viewer = { items, index: Math.max(0, items.findIndex(i => i.id === id)) }
        },
        openLocal(url) {
            this.viewer = { items: [{ url }], index: 0 }
        },
        moveViewer(step) {
            const count = this.viewer.items.length
            this.viewer.index = (this.viewer.index + step + count) % count
        },
        onKeydown(event) {
            if (this.viewer == null || this.viewer.items.length < 2) return
            if (event.key === 'ArrowLeft') this.moveViewer(-1)
            if (event.key === 'ArrowRight') this.moveViewer(1)
        },

        send({ content, files }) {
            const message = {
                id: 'local-' + (++localId),
                ticketId: this.ticket.id,
                isEvent: false,
                content,
                resources: [],
                files,
                localPhotos: files.map(f => URL.createObjectURL(f)),
                sentDate: new Date(),
                creatorId: this.myUserId,
                creatorName: this.$store.getters.user.fullName,
                state: 'sending'
            }
            this.pending.push(message)
            this.$nextTick(this.scrollToBottom)
            this.post(message)
        },
        retry(message) {
            message.state = 'sending'
            this.post(message)
        },
        async post(message) {
            const form = new FormData()
            form.append('ticketId', message.ticketId)
            form.append('content', message.content)
            message.files.forEach(f => form.append('resources', f))

            try {
                await sendMessage(form)
            } catch (e) {
                message.state = 'failed'
                return
            }

            // Dociągamy wątek (już ze zdjęciami z serwera) i dopiero wtedy zdejmujemy lokalną kopię,
            // żeby wiadomość nie mignęła.
            if (message.ticketId == this.ticket.id) {
                await this.load({ silent: true })
            }
            this.pending = this.pending.filter(p => p !== message)
            message.localPhotos.forEach(url => URL.revokeObjectURL(url))
        },

        async closeTicket() {
            await this.changeStatus(confirmRequestToClose)
        },
        async denyClose() {
            await this.changeStatus(denyRequestToClose)
        },
        async changeStatus(action) {
            this.busy = true
            try {
                await action(this.ticket.id)
                this.confirmingClose = false
                await this.load({ silent: true })
                this.$emit('changed', this.ticket.id)
            } finally {
                this.busy = false
            }
        },

        onDragOver(event) {
            if (this.status == TicketStatus.Closed) return
            const types = event.dataTransfer ? Array.from(event.dataTransfer.types) : []
            this.dragging = types.includes('Files')
        },
        onDragLeave(event) {
            // dragleave przychodzi też przy wejściu nad dziecko — liczy się tylko wyjście z panelu.
            if (!this.$el.contains(event.relatedTarget)) this.dragging = false
        },
        onDrop(event) {
            this.dragging = false
            if (this.$refs.composer && event.dataTransfer) {
                this.$refs.composer.addFiles(event.dataTransfer.files)
                this.$refs.composer.focus()
            }
        },

        isNearBottom() {
            const list = this.$refs.list
            return list == null || list.scrollHeight - list.scrollTop - list.clientHeight < NEAR_BOTTOM_PX
        },
        scrollToBottom() {
            const list = this.$refs.list
            if (list) list.scrollTop = list.scrollHeight
        },

        eventText(message) {
            const key = EVENT_KEYS[message.content]
            return key ? this.$t(key) : message.content
        },
        initials,
        dayLabel(date) {
            const today = new Date()
            const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
            if (dayKey(date) === dayKey(today)) return this.$t('Today')
            if (dayKey(date) === dayKey(yesterday)) return this.$t('Yesterday')
            return moment(date).format('YYYY-MM-DD')
        },
        formatTime(date) {
            return moment(date).format('HH:mm')
        },
        formatDay(date) {
            return moment(date).format('YYYY-MM-DD')
        },
        formatFull(date) {
            return date ? moment(date).format('YYYY-MM-DD HH:mm') : ''
        }
    }
}
</script>

<style scoped>
.ticket-chat {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: var(--messages-panel-bg);
    color: var(--text);
}

/* --- Nagłówek --- */

.ticket-chat__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--messages-panel-border);
    background: var(--chat-header-bg);
}

.ticket-chat__back {
    border: none;
    background: transparent;
    color: var(--text);
    padding: 4px;
    border-radius: 50%;
    cursor: pointer;
}

.ticket-chat__title {
    flex: 1;
    min-width: 0;
}

.ticket-chat__subject {
    font-weight: 600;
    font-size: 15px;
    color: var(--text-strong);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ticket-chat__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--chat-muted);
    margin-top: 2px;
}

.ticket-chat__status {
    padding: 1px 8px;
    border-radius: 10px;
    font-weight: 600;
    background: var(--chat-status-bg);
    color: var(--chat-status-fg);
}

.ticket-chat__status--0 { background: var(--status-warning-tint); }
.ticket-chat__status--2 { background: var(--status-warning-tint); }
.ticket-chat__status--3 { background: var(--status-error-tint); }
.ticket-chat__status--4 { background: var(--status-down-tint); }

.ticket-chat__actions,
.ticket-chat__banner-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.ticket-chat__confirm {
    font-size: 13px;
}

.ticket-chat__action {
    border: 1px solid var(--input-border);
    background: var(--surface);
    color: var(--text);
    border-radius: 6px;
    padding: 4px 12px;
    font-size: 13px;
    cursor: pointer;
}

.ticket-chat__action:hover {
    background: var(--surface-hover-solid);
}

.ticket-chat__action:disabled {
    opacity: 0.5;
    cursor: default;
}

.ticket-chat__action--primary {
    background: var(--chat-bubble-mine-bg);
    border-color: var(--chat-bubble-mine-bg);
    color: var(--chat-bubble-mine-fg);
}

.ticket-chat__action--primary:hover {
    background: var(--chat-bubble-mine-bg);
    filter: brightness(1.1);
}

.ticket-chat__action--danger {
    background: var(--danger);
    border-color: var(--danger);
    color: #fff;
}

.ticket-chat__action--danger:hover {
    background: var(--danger);
    filter: brightness(1.1);
}

/* --- Lista --- */

.ticket-chat__list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 14px;
}

.ticket-chat__day {
    display: flex;
    justify-content: center;
    margin: 14px 0 6px;
}

.ticket-chat__day span,
.ticket-chat__event span {
    font-size: 12px;
    color: var(--chat-muted);
    background: var(--chat-event-bg);
    padding: 2px 10px;
    border-radius: 10px;
}

.ticket-chat__event {
    display: flex;
    justify-content: center;
    margin: 8px 0;
}

.ticket-chat__event span {
    font-style: italic;
}

.ticket-chat__notice {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 40px;
    color: var(--chat-muted);
    text-align: center;
}

.ticket-chat__skeleton-bubble {
    height: 44px;
    width: 55%;
    margin: 10px 0;
    border-radius: 14px;
    background: var(--skeleton);
}

.ticket-chat__skeleton-bubble--mine {
    margin-left: auto;
    width: 40%;
}

/* --- Wiadomości --- */

.chat-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-top: 2px;
}

.chat-row--start {
    margin-top: 10px;
}

.chat-row--mine {
    justify-content: flex-end;
}

.chat-avatar {
    flex: 0 0 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--chat-avatar-bg);
    color: var(--chat-avatar-fg);
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    margin-top: 18px;
}

.chat-avatar--hidden {
    visibility: hidden;
    height: 0;
    margin-top: 0;
}

.chat-column {
    display: flex;
    flex-direction: column;
    max-width: 78%;
    min-width: 0;
}

.chat-row--mine .chat-column {
    align-items: flex-end;
}

.chat-author {
    font-size: 12px;
    color: var(--chat-muted);
    margin: 0 0 2px 4px;
}

.chat-bubble {
    padding: 7px 12px;
    border-radius: 6px 16px 16px 6px;
    background: var(--chat-bubble-bg);
    color: var(--chat-bubble-fg);
    font-size: 14px;
    line-height: 1.4;
}

.chat-bubble--start { border-top-left-radius: 16px; }
.chat-bubble--end { border-bottom-left-radius: 16px; }

.chat-bubble--mine {
    border-radius: 16px 6px 6px 16px;
    background: var(--chat-bubble-mine-bg);
    color: var(--chat-bubble-mine-fg);
}

.chat-bubble--mine.chat-bubble--start { border-top-right-radius: 16px; }
.chat-bubble--mine.chat-bubble--end { border-bottom-right-radius: 16px; }

.chat-bubble--pending {
    opacity: 0.6;
}

.chat-bubble--failed {
    outline: 2px solid var(--danger);
}

.chat-bubble__text {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.chat-bubble__images {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 4px -6px -2px;
}

.chat-bubble__text + .chat-bubble__images {
    margin-top: 8px;
}

.chat-time {
    font-size: 11px;
    color: var(--chat-muted);
    margin: 2px 4px 0;
}

.chat-time--failed {
    color: var(--danger);
}

.chat-retry {
    border: none;
    background: transparent;
    color: var(--accent);
    text-decoration: underline;
    padding: 0 0 0 4px;
    font-size: 11px;
    cursor: pointer;
}

/* --- Dół panelu --- */

.ticket-chat__banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 14px;
    background: var(--status-warning-tint);
    border-top: 1px solid var(--messages-panel-border);
    font-size: 14px;
    font-weight: 600;
}

.ticket-chat__closed {
    padding: 12px;
    text-align: center;
    font-size: 13px;
    color: var(--chat-muted);
    border-top: 1px solid var(--messages-panel-border);
}

.ticket-chat__drop {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--chat-drop-bg);
    border: 2px dashed var(--accent);
    border-radius: 5px;
    color: var(--text-strong);
    font-weight: 600;
    pointer-events: none;
}

/* --- Podgląd zdjęć --- */

.chat-viewer {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 5px;
}

.chat-viewer__img {
    max-width: 70vw;
    max-height: 70vh;
}

.chat-viewer__placeholder {
    width: 40vw;
    height: 50vh;
    background: var(--skeleton);
}

.chat-viewer__nav {
    border: none;
    background: var(--surface-hover);
    color: var(--text);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
}

.chat-viewer__counter {
    position: absolute;
    bottom: -24px;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 12px;
    color: var(--chat-muted);
}
</style>



// WEBPACK FOOTER //
// src/components/Systems/Tickets/TicketChat.vue